import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutList, Table2, Filter, ChevronLeft, ChevronRight, Download,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { useMobileStore } from '@/stores/mobile-store';
import { MobileActionSheet } from '../MobileActionSheet';
import { isDocumentDriver } from '@/lib/driver-kind';
import type { ConnectionConfig, QueryResult } from '@/types';

interface DataViewScreenProps {
  params: Record<string, unknown>;
}

const PAGE_SIZE = 50;

export function DataViewScreen({ params }: DataViewScreenProps) {
  const connectionId = params.connectionId as string;
  const schemaName = params.schema as string;
  const tableName = params.table as string;
  const pushScreen = useMobileStore((s) => s.pushScreen);

  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'card' | 'grid'>('card');
  const [whereClause, setWhereClause] = useState('');
  const [orderByClause, setOrderByClause] = useState('');
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const [orderByInput, setOrderByInput] = useState('');
  const [headerActionSheet, setHeaderActionSheet] = useState(false);

  // Resolve driver kind so we build the right query language. Same
  // pattern as desktop TableView — subscribe to the connections query
  // rather than reading the cache once, otherwise the kind freezes at
  // the default and a Mongo collection gets a SQL query.
  const { data: connections } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
    staleTime: 60_000,
  });
  const driverType = useMemo(() => {
    return connections?.find((c) => c.id === connectionId)?.driverType ?? 'postgresql';
  }, [connections, connectionId]);
  const isDocDriver = isDocumentDriver(driverType);

  const sql = useMemo(() => {
    if (isDocDriver) {
      // Mongo: build a mongosh expression. Empty WHERE becomes the
      // no-op `{}` filter. ORDER BY is passed straight as a sort doc.
      const filter = whereClause.trim() || '{}';
      const sort = orderByClause.trim() ? `.sort(${orderByClause.trim()})` : '';
      return `db.${tableName}.find(${filter})${sort}`;
    }
    const base = `SELECT * FROM ${schemaName}.${tableName}`;
    const where = whereClause.trim() ? ` WHERE ${whereClause.trim()}` : '';
    const orderBy = orderByClause.trim() ? ` ORDER BY ${orderByClause.trim()}` : '';
    return `${base}${where}${orderBy}`;
  }, [isDocDriver, schemaName, tableName, whereClause, orderByClause]);

  const { data, isLoading, isFetching, error } = useQuery<QueryResult>({
    // driverType in the key so a tab restored before connections load
    // re-runs once the real driver is known. Matches desktop fix.
    queryKey: ['table-data', connectionId, schemaName, tableName, whereClause, orderByClause, page, driverType],
    queryFn: () =>
      apiClient.post<QueryResult>('/api/query/execute', {
        connectionId,
        sql,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: !!connectionId,
    staleTime: 30_000,
  });

  const totalPages = useMemo(() => {
    if (!data?.totalRows) return 1;
    return Math.max(1, Math.ceil(data.totalRows / PAGE_SIZE));
  }, [data?.totalRows]);

  const handleCardTap = useCallback(
    (row: Record<string, unknown>, index: number) => {
      pushScreen({
        type: 'record-detail',
        title: `Record #${(page - 1) * PAGE_SIZE + index + 1}`,
        params: {
          connectionId,
          schema: schemaName,
          table: tableName,
          rowData: row,
          columns: data?.columns ?? [],
        },
      });
    },
    [pushScreen, connectionId, schemaName, tableName, page, data?.columns],
  );

  const handleApplyFilter = useCallback(() => {
    setWhereClause(filterInput);
    setOrderByClause(orderByInput);
    setPage(1);
    setShowFilterSheet(false);
  }, [filterInput, orderByInput]);

  const handleClearFilter = useCallback(() => {
    setFilterInput('');
    setOrderByInput('');
    setWhereClause('');
    setOrderByClause('');
    setPage(1);
    setShowFilterSheet(false);
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div
        className="flex items-center gap-2 px-3 shrink-0 border-b"
        style={{
          height: 44,
          backgroundColor: 'var(--dbgo-panel)',
          borderColor: 'var(--border-default)',
        }}
      >
        <button
          onClick={() => setViewMode(viewMode === 'card' ? 'grid' : 'card')}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium active:opacity-70"
          style={{ color: 'var(--accent-blue)' }}
        >
          {viewMode === 'card' ? (
            <>
              <Table2 size={14} />
              Grid
            </>
          ) : (
            <>
              <LayoutList size={14} />
              Card
            </>
          )}
        </button>

        <button
          onClick={() => {
            setFilterInput(whereClause);
            setOrderByInput(orderByClause);
            setShowFilterSheet(true);
          }}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium active:opacity-70"
          style={{
            color:
              whereClause || orderByClause
                ? 'var(--accent-yellow)'
                : 'var(--text-secondary)',
          }}
        >
          <Filter size={14} />
          Filter
          {(whereClause || orderByClause) && <span className="text-[10px]">(active)</span>}
        </button>

        <div className="flex-1" />

        <button
          onClick={() => setHeaderActionSheet(true)}
          className="p-1.5 text-text-muted active:text-accent-blue"
        >
          <Download size={16} />
        </button>

        {isFetching && (
          <div
            className="w-3 h-3 rounded-full border-2 animate-spin"
            style={{ borderColor: 'var(--text-muted)', borderTopColor: 'var(--accent-blue)' }}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-lg animate-pulse"
                style={{ height: 120, backgroundColor: 'var(--dbgo-panel)' }}
              />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-center text-accent-red text-sm">
            {error instanceof Error ? error.message : 'Failed to load data'}
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-4 text-center text-text-muted text-sm py-12">
            No data found
          </div>
        ) : viewMode === 'card' ? (
          <CardView
            columns={data.columns}
            rows={data.rows}
            onCardTap={handleCardTap}
          />
        ) : (
          <GridView columns={data.columns} rows={data.rows} />
        )}
      </div>

      {/* Pagination */}
      {data && data.rows.length > 0 && (
        <div
          className="flex items-center justify-between px-4 shrink-0 border-t"
          style={{
            height: 44,
            backgroundColor: 'var(--dbgo-panel)',
            borderColor: 'var(--border-default)',
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 text-sm disabled:opacity-30 active:opacity-70"
            style={{ color: 'var(--accent-blue)' }}
          >
            <ChevronLeft size={16} />
            Prev
          </button>
          <span className="text-xs text-text-secondary">
            Page {page} of {totalPages}
            {data.totalRows != null && (
              <span className="text-text-muted"> ({data.totalRows.toLocaleString()} rows)</span>
            )}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="flex items-center gap-1 text-sm disabled:opacity-30 active:opacity-70"
            style={{ color: 'var(--accent-blue)' }}
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Filter bottom sheet */}
      {showFilterSheet && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setShowFilterSheet(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full rounded-t-xl p-4"
            style={{ backgroundColor: 'var(--dbgo-panel)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pb-3">
              <div
                className="rounded-full"
                style={{ width: 40, height: 4, backgroundColor: 'var(--border-strong)' }}
              />
            </div>
            <div className="text-xs font-medium text-text-muted mb-1">
              {isDocDriver ? 'Filter (mongosh)' : 'WHERE clause'}
            </div>
            <input
              type="text"
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              placeholder={isDocDriver
                ? '{ status: "active" }'
                : "e.g. status = 'active' AND id > 100"}
              className="w-full rounded-lg px-3 text-sm text-text-primary outline-none placeholder:text-text-muted"
              style={{
                height: 44,
                backgroundColor: 'var(--dbgo-input)',
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApplyFilter();
              }}
            />

            <div className="text-xs font-medium text-text-muted mt-4 mb-1">
              {isDocDriver ? 'Sort (mongosh)' : 'ORDER BY'}
            </div>
            <input
              type="text"
              value={orderByInput}
              onChange={(e) => setOrderByInput(e.target.value)}
              placeholder={isDocDriver ? '{ createdAt: -1 }' : 'e.g. created_at DESC, id ASC'}
              className="w-full rounded-lg px-3 text-sm text-text-primary outline-none placeholder:text-text-muted"
              style={{
                height: 44,
                backgroundColor: 'var(--dbgo-input)',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApplyFilter();
              }}
            />

            <div className="flex gap-2 mt-3">
              <button
                onClick={handleClearFilter}
                className="flex-1 rounded-lg text-sm font-medium active:opacity-70"
                style={{
                  height: 44,
                  color: 'var(--text-secondary)',
                  backgroundColor: 'var(--dbgo-root)',
                }}
              >
                Clear
              </button>
              <button
                onClick={handleApplyFilter}
                className="flex-1 rounded-lg text-sm font-medium text-white active:opacity-70"
                style={{
                  height: 44,
                  backgroundColor: 'var(--accent-blue)',
                }}
              >
                Apply
              </button>
            </div>
            <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
          </div>
        </div>
      )}

      {/* Header action sheet */}
      {headerActionSheet && (
        <MobileActionSheet
          items={[
            {
              label: 'Export (use desktop)',
              icon: Download,
              onClick: () => {
                // Export is complex, defer to desktop
              },
              disabled: true,
            },
          ]}
          onClose={() => setHeaderActionSheet(false)}
        />
      )}
    </div>
  );
}

// ─── Card View ──────────────────────────────────────

interface CardViewProps {
  columns: QueryResult['columns'];
  rows: Record<string, unknown>[];
  onCardTap: (row: Record<string, unknown>, index: number) => void;
}

function CardView({ columns, rows, onCardTap }: CardViewProps) {
  // Show at most 6 fields per card to keep cards compact
  const displayColumns = columns.slice(0, 6);
  const hasMore = columns.length > 6;

  return (
    <div className="p-4 space-y-3">
      {rows.map((row, i) => (
        <button
          key={i}
          onClick={() => onCardTap(row, i)}
          className="w-full text-left rounded-lg p-3 border active:opacity-70 transition-opacity"
          style={{
            backgroundColor: 'var(--dbgo-panel)',
            borderColor: 'var(--border-default)',
          }}
        >
          {displayColumns.map((col) => (
            <div key={col.name} className="flex items-baseline gap-2 py-0.5">
              <span className="text-xs text-text-muted shrink-0 w-24 truncate">
                {col.name}:
              </span>
              <span className="text-sm text-text-primary truncate flex-1">
                {formatCellValue(row[col.name])}
              </span>
            </div>
          ))}
          {hasMore && (
            <div className="text-xs text-text-muted mt-1 pt-1 border-t" style={{ borderColor: 'var(--border-default)' }}>
              +{columns.length - 6} more fields
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Grid View ──────────────────────────────────────

interface GridViewProps {
  columns: QueryResult['columns'];
  rows: Record<string, unknown>[];
}

function GridView({ columns, rows }: GridViewProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: 'var(--dbgo-panel)' }}>
            {columns.map((col) => (
              <th
                key={col.name}
                className="text-left px-3 text-xs font-medium text-text-muted whitespace-nowrap border-b"
                style={{ height: 36, borderColor: 'var(--border-default)' }}
              >
                {col.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b"
              style={{ borderColor: 'var(--border-default)' }}
            >
              {columns.map((col) => (
                <td
                  key={col.name}
                  className="px-3 text-text-primary whitespace-nowrap truncate"
                  style={{ height: 40, maxWidth: 200 }}
                >
                  {formatCellValue(row[col.name])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
